import { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { PiListLight } from "react-icons/pi";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GlobalContext } from "../context";
import Sidebar from "../components/Dashboard/Sidebar";
import NotificationBell from "../components/Dashboard/NotificationBell";
import Logo from "../components/AllVon/VonHeader/assets/logo.png";
import LmsShell from "../components/Dashboard/learn/LmsShell";
import StaffShell from "../components/Dashboard/learn/StaffShell";
import { InstallAppPrompt } from "../pwa";

export default function DashboardLayout({ children }) {
  const [collapsed,  setCollapsed]  = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { authState: { data } } = useContext(GlobalContext);
  const user     = data?.user;
  const initials = user?.firstname?.[0]?.toUpperCase() || "U";

  // Students get the LMS revamp shell (prototype sidebar).
  if (user && !user.is_admin && !user.is_instructor) {
    return <><LmsShell>{children}</LmsShell><InstallAppPrompt /></>;
  }
  // Admins and instructors get the same new shell with the staff menus (old layout kept below as a fallback
  // while the remaining admin pages move over).
  if (user && (user.is_admin || user.is_instructor)) {
    return <><StaffShell>{children}</StaffShell><InstallAppPrompt /></>;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">

      {/* ── Full-width top bar — logo always visible ── */}
      <header className="bg-white border-b border-gray-200 h-16 px-4 md:px-6
        flex items-center justify-between flex-shrink-0 z-30 sticky top-0">

        <div className="flex items-center gap-6">
          {/* Logo — bold, always present */}
          <Link to="/" className="flex-shrink-0">
            <img src={Logo} alt="E1X Academy" className="h-16 w-auto object-contain" />
          </Link>

          {/* Separator */}
          <div className="hidden lg:block w-px h-6 bg-gray-200" />

          {/* Desktop: collapse / expand sidebar */}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-md
              text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200/80 transition-colors"
            aria-label="Toggle sidebar"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          {/* Mobile: open drawer */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-md
              text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <PiListLight size={22} />
          </button>
        </div>

        {/* User info + avatar */}
        <div className="flex items-center gap-3">
          {(user?.is_admin || user?.is_instructor) && <NotificationBell />}
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium text-gray-800 leading-tight">
              {user?.firstname} {user?.lastname}
            </p>
            <p className="text-xs text-gray-400 leading-tight">{user?.email}</p>
          </div>

          {user?.profile_img ? (
            <img
              src={user.profile_img}
              alt="avatar"
              className="w-9 h-9 rounded-full object-cover border-2 border-[#F05A19] flex-shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#F05A19] text-white flex items-center
              justify-center font-semibold text-sm flex-shrink-0">
              {initials}
            </div>
          )}
        </div>
      </header>

      {/* ── Sidebar + content row ── */}
      <div className="flex flex-1">
        <Sidebar
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />

        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>

    </div>
  );
}
